"use client"

import type {
  HTMLProps,
  HTMLStyledProps,
  ReactNodeOrFunction,
  SimplePlacement,
  ThemeProps,
} from "@/components/ui"
import type {
  HTMLMotionProps,
  MotionTransitionProps,
} from "@yamada-ui/react/components/motion"
import type { PortalProps } from "@yamada-ui/react/components/portal"
import type { FC, PropsWithChildren } from "react"
import type { PopoverStyle } from "./popover.style"
import type { UsePopoverProps, UsePopoverReturn } from "./use-popover"
import { fadeScaleVariants } from "@yamada-ui/react/components/fade-scale"
import { motion } from "@yamada-ui/react/components/motion"
import { Portal } from "@yamada-ui/react/components/portal"
import { slideFadeVariants } from "@yamada-ui/react/components/slide-fade"
import { AnimatePresence } from "motion/react"
import { useMemo } from "react"
import { cast, createSlotComponent, runIfFn } from "@/components/ui"
import { popoverStyle } from "./popover.style"
import { usePopover } from "./use-popover"

export interface PopupAnimationProps {
  /**
   * The animation of the element.
   *
   * @default 'scale'
   */
  animationScheme?: "none" | "scale" | SimplePlacement
  /**
   * The animation duration.
   *
   * @default 0.2
   */
  duration?: MotionTransitionProps["duration"]
}

export const getPopupAnimationProps = (
  animationScheme: PopupAnimationProps["animationScheme"] = "scale",
  duration?: PopupAnimationProps["duration"],
) => {
  const sharedProps = { animate: "enter", exit: "exit", initial: "exit" }

  switch (animationScheme) {
    case "scale":
      return {
        ...sharedProps,
        custom: { duration, reverse: true, scale: 0.95 },
        variants: fadeScaleVariants,
      }
    case "block-start":
      return {
        ...sharedProps,
        custom: { duration, offsetY: -16, reverse: true },
        variants: slideFadeVariants,
      }
    case "inline-end":
      return {
        ...sharedProps,
        custom: { duration, offsetX: 16, reverse: true },
        variants: slideFadeVariants,
      }
    case "inline-start":
      return {
        ...sharedProps,
        custom: { duration, offsetX: -16, reverse: true },
        variants: slideFadeVariants,
      }
    case "block-end":
      return {
        ...sharedProps,
        custom: { duration, offsetY: 16, reverse: true },
        variants: slideFadeVariants,
      }
    default:
      return {}
  }
}

interface ComponentContext
  extends Pick<
      UsePopoverReturn,
      | "getAnchorProps"
      | "getBodyProps"
      | "getContentProps"
      | "getFooterProps"
      | "getHeaderProps"
      | "getPositionerProps"
      | "getTriggerProps"
      | "open"
    >,
    PopupAnimationProps,
    Pick<PopoverRootProps, "withCloseButton"> {}

export interface PopoverRootProps
  extends UsePopoverProps,
    PopupAnimationProps,
    ThemeProps<PopoverStyle> {
  /**
   * The children of the popover.
   */
  children?: ReactNodeOrFunction<{
    open: boolean
    onClose: () => void
  }>
  /**
   * The animation duration.
   *
   * @default 0.2
   */
  duration?: PopupAnimationProps["duration"]
  /**
   * If `true`, display the popover close button.
   *
   * @default true
   */
  withCloseButton?: boolean
}

const {
  ComponentContext,
  PropsContext: PopoverPropsContext,
  StyleContext,
  useComponentContext,
  usePropsContext: usePopoverPropsContext,
  withContext,
  useRootComponentProps,
} = createSlotComponent<PopoverRootProps, PopoverStyle, ComponentContext>(
  "popover",
  popoverStyle,
)

export { PopoverPropsContext, usePopoverPropsContext }

/**
 * `Popover` is a component that floats around an element to display information.
 *
 * @see https://yamada-ui.com/docs/components/popover
 */
export const PopoverRoot: FC<PopoverRootProps> = (props) => {
  const [
    styleContext,
    {
      animationScheme = "scale",
      children,
      duration = 0.1,
      withCloseButton = true,
      ...rest
    },
  ] = useRootComponentProps(props)
  const {
    open,
    getAnchorProps,
    getBodyProps,
    getContentProps,
    getFooterProps,
    getHeaderProps,
    getPositionerProps,
    getTriggerProps,
    onClose,
  } = usePopover(rest)
  const componentContext = useMemo(
    () => ({
      animationScheme,
      duration,
      open,
      withCloseButton,
      getAnchorProps,
      getBodyProps,
      getContentProps,
      getFooterProps,
      getHeaderProps,
      getPositionerProps,
      getTriggerProps,
    }),
    [
      withCloseButton,
      open,
      animationScheme,
      duration,
      getAnchorProps,
      getBodyProps,
      getContentProps,
      getFooterProps,
      getHeaderProps,
      getPositionerProps,
      getTriggerProps,
    ],
  )

  return (
    <StyleContext value={styleContext}>
      <ComponentContext value={componentContext}>
        {runIfFn(children, { open, onClose })}
      </ComponentContext>
    </StyleContext>
  )
}

export interface PopoverTriggerProps extends HTMLStyledProps<"button"> {}

export const PopoverTrigger = withContext<"button", PopoverTriggerProps>(
  "button",
  "trigger",
)({ asChild: true }, (props) => {
  const { getTriggerProps } = useComponentContext()

  return getTriggerProps(props)
})

export interface PopoverAnchorProps extends HTMLStyledProps {}

export const PopoverAnchor = withContext<"div", PopoverAnchorProps>(
  "div",
  "anchor",
)({ asChild: true }, (props) => {
  const { getAnchorProps } = useComponentContext()

  return getAnchorProps(props)
})

interface PopoverPositionerProps extends HTMLStyledProps {}

const PopoverPositioner = withContext<"div", PopoverPositionerProps>(
  "div",
  "positioner",
)(undefined, (props) => {
  const { getPositionerProps } = useComponentContext()

  return getPositionerProps(props)
})

export interface PopoverContentProps
  extends Omit<HTMLMotionProps, "children">,
    PropsWithChildren {
  /**
   * Props for portal component.
   */
  portalProps?: Omit<PortalProps, "children">
}

export const PopoverContent = withContext<"div", PopoverContentProps>(
  ({ portalProps, ...rest }) => {
    const { animationScheme, duration, open, getContentProps } =
      useComponentContext()

    return (
      <AnimatePresence>
        {open ? (
          <Portal {...portalProps}>
            <PopoverPositioner>
              <motion.div
                {...getPopupAnimationProps(animationScheme, duration)}
                {...cast<HTMLMotionProps>(
                  getContentProps(cast<HTMLProps>(rest)),
                )}
              />
            </PopoverPositioner>
          </Portal>
        ) : null}
      </AnimatePresence>
    )
  },
  "content",
)()

export interface PopoverHeaderProps extends HTMLStyledProps {}

export const PopoverHeader = withContext<"div", PopoverHeaderProps>(
  "div",
  "header",
)(undefined, (props) => {
  const { getHeaderProps } = useComponentContext()

  return getHeaderProps(props)
})

export interface PopoverBodyProps extends HTMLStyledProps {}

export const PopoverBody = withContext<"div", PopoverBodyProps>("div", "body")(
  undefined,
  (props) => {
    const { getBodyProps } = useComponentContext()

    return getBodyProps(props)
  },
)

export interface PopoverFooterProps extends HTMLStyledProps {}

export const PopoverFooter = withContext<"div", PopoverFooterProps>(
  "div",
  "footer",
)(undefined, (props) => {
  const { getFooterProps } = useComponentContext()

  return getFooterProps(props)
})
